import { selectProfileData } from '../../../store/workspace/selectors';
import { getProfileDataThunk } from '../../../store/workspace/thunks';
import { useScreenRecorder } from '../../../hooks/useScreenRecorder';
import { useVideoCapture } from '../../../hooks/useVideoCapture';
import { uploadFileThunk } from '../../../store/general/thunks';
import { useDispatch, useSelector } from '../../../store/store';
import CollectingData from '../../../containers/CollectingData';
import { CodingExamQuestion } from '../../../store/exam/types';
import { getUserInfoThunk } from '../../../store/auth/thunks';
import MotionWrapper from '../../../containers/MotionWrapper';
import { selectUserId } from '../../../store/auth/selectors';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCountDown } from '../../../hooks/useCountDown';
import Button from '../../../components/Button';
import ExamBreak from './containers/ExamBreak';
import { useEffect, useState } from 'react';
import ExamEnd from './containers/ExamEnd';
import PreExam from './containers/PreExam';
import KYC from '../../../containers/KYC';
import styles from './style.module.scss';
import Exam from './containers/Exam';
import Webcam from 'react-webcam';
import {
    TypographyLogo,
    TypographyLogoLightIcon,
} from '../../../assets/icons/common';
import {
    postCodingExamAnswerThunk,
    getCodingExamQuestionThunk,
} from '../../../store/exam/thunks';
import {
    setExitExamModal,
    setCodingExamStep,
    setCodingExamQuestion,
    setCodingExamBreakModal,
    setCodeEditorOutput,
} from '../../../store/exam/slice';
import {
    selectSelectedExam,
    selectCodingExamStep,
    selectCodingExamQuestion,
    selectFetchingCodingExam,
    selectCodingExamBreakModal,
} from '../../../store/exam/selectors';

export default function CodingExam() {
    const [q, setQ] = useState(1);
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [answer, setAnswer] = useState('');
    const userId = useSelector(selectUserId);
    const profile = useSelector(selectProfileData);
    const step = useSelector(selectCodingExamStep);
    const fetching = useSelector(selectFetchingCodingExam);
    // const examData = useSelector(selectCodingExamQuestion);
    const [cameraAccess, setCameraAccess] = useState(true);
    const breakModal = useSelector(selectCodingExamBreakModal);
    const selectedExamDetail = useSelector(selectSelectedExam);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [selectedLang, setSelectedLang] = useState('');
    const [isFetching, setIsFetching] = useState(false);
    const [examData, setExamData] = useState<CodingExamQuestion | null>(null);

    const { counter, pause, restart } = useCountDown();
    const action = location?.state?.action;
    const examId = location?.state?.examId;
    const jobId = location?.state?.jobId;
    const langs = location?.state?.langs;

    useEffect(() => {
        !userId && dispatch(getUserInfoThunk());
        !profile && dispatch(getProfileDataThunk());
    }, []);

    const {
        recordFile,
        stopRecording,
        startRecording,
        screenFileName,
        setScreenRecordChunk,
        recording: recordingScreen,
        error: screenHasRecordError,
    } = useScreenRecorder();

    const {
        webcamRef,
        streamFile,
        stopCapture,
        startCapture,
        streamFileName,
    } = useVideoCapture();

    useEffect(() => {
        if (!location?.state?.jobId) {
            navigate('/workspace');
        }

        return () => {
            stopRecording();
            dispatch(setCodeEditorOutput(null));
            dispatch(setCodingExamQuestion(null));
            dispatch(setCodingExamStep('KYC'));
        }
    }, []);

    useEffect(() => {
        if (step === 'PreExam') {
            startRecording();
        }

        // if (step === 'Exam') {
        //     // startCapture();
        //     restart();
        // }
    }, [step]);

    useEffect(() => {
        uploadStreamChunk();
    }, [streamFile]);

    useEffect(() => {
        uploadScreenRecord();
    }, [recordFile]);

    useEffect(() => {
        if (examData && counter === 0 && step === 'Exam') {
            submit();
        }
    }, [counter]);

    useEffect(() => {
        if (step === 'CollectingData') {
            dispatch(setCodingExamStep('ExamEnd'));
        }
    }, [uploadedFiles]);

    const submit = () => {
        setIsFetching(true);
        if (window.getSelection) {
            window.getSelection()?.removeAllRanges();
        }

        const body = {
            answer,
            language: examData?.language,
            jobId: location?.state?.jobId,
        };

        pause();

        dispatch(postCodingExamAnswerThunk(body))
            .unwrap()
            .then(res => {
                stopCapture();

                if (q === 3) {
                    dispatch(setCodingExamStep('CollectingData'));
                    stopRecording();
                } else {
                    dispatch(setCodingExamBreakModal(true));
                    setScreenRecordChunk();

                    // await dispatch(getCodingExamQuestionThunk({
                    //     userId,
                    //     question: q,
                    //     language: examData?.language,
                    //     jobId: location?.state?.jobId,
                    // }));

                    // stopCapture();
                    // startCapture();
                    // setScreenRecordChunk();

                    // startRecording();
                    // restart();
                    // setQ(prev => prev + 1);
                }
            })
            .catch(err => {
                console.warn(err);
            })
            .finally(() => {
                setIsFetching(false);
            });
    };

    const startExam = () => {
        if (!location?.state?.jobId || !location?.state?.langs) return;
        const language = location?.state?.langs?.length > 1 ? selectedLang : location?.state?.langs[0];

        setIsFetching(true);
        dispatch(getCodingExamQuestionThunk({
            language,
            jobId: location.state.jobId,
        }))
            .unwrap()
            .then(({ statusCode, body }) => {
                if (statusCode === 208) {
                    const body = {
                        answer,
                        jobId: location?.state?.jobId,
                    };

                    dispatch(postCodingExamAnswerThunk(body))
                        .unwrap()
                        .then(() => {
                            dispatch(setCodingExamStep('ExamEnd'));
                        })
                        .catch(err => {
                            console.warn(err);
                        })
                        .finally(() => {
                            setIsFetching(false);
                        });
                } else {
                    setExamData(body);
                    setIsFetching(false);
                    setQ(body.questionNumber);
                    dispatch(setCodingExamStep('Exam'));
                    restart(body.solving_time * 60);
                }
            })
            .catch(err => {
                console.warn(err);
                setIsFetching(false);
            })
    };

    const getNextQuestion = () => {
        setIsFetching(true);
        dispatch(getCodingExamQuestionThunk({
            // language: examData?.language,
            jobId: location?.state?.jobId,
        }))
            .unwrap()
            .then(({ body }) => {
                setExamData(body);
                dispatch(setCodeEditorOutput(null));
                dispatch(setCodingExamBreakModal(false));
                // stopCapture();
                startCapture();
                // setScreenRecordChunk();
                startRecording();
                restart(body.solving_time * 60);
                setQ(prev => prev + 1);
            })
            .catch(err => {
                console.warn(err);
            })
            .finally(() => {
                setIsFetching(false);
            });
    };

    const updateUploadProgress = (percentage: number) => {
        setFileUploadProgress(percentage)
    };

    const uploadStreamChunk = async () => {
        if (!examData || !streamFile) return;
        // const fileName = `${userId}/${jobId}/${examId}/${streamFileName}`;
        const fileName = `${userId}/${jobId}/${examId}/stream_${q}.webm`;

        await dispatch(uploadFileThunk({
            fileName,
            file: streamFile
        }));

        setUploadedFiles(prev => [...prev, streamFileName]);
    };

    const uploadScreenRecord = async () => {
        if (!examData || !recordFile) return;
        // const fileName = `${userId}/${jobId}/${examId}/${screenFileName}`;
        const fileName = `${userId}/${jobId}/${examId}/screen_record_part_${q}.webm`;

        await dispatch(uploadFileThunk({
            file: recordFile,
            callback: updateUploadProgress,
            fileName,
        }));

        setUploadedFiles(prev => [...prev, screenFileName]);
    };

    const checkCameraStatus = () => {
        const videoTrack = webcamRef.current?.stream?.getVideoTracks()[0];
        if (videoTrack && (!videoTrack.enabled || videoTrack.readyState === 'ended')) {
            console.error('Camera access was blocked after being granted');
            setIsCameraActive(false);
            setCameraAccess(false);
        }
    };

    useEffect(() => {
        // Listen for changes to media devices (e.g., camera disconnect)
        navigator.mediaDevices.ondevicechange = checkCameraStatus;

        // Periodically check the camera status (optional, for robust error checking)
        const interval = setInterval(checkCameraStatus, 1000);

        return () => {
            clearInterval(interval);
            navigator.mediaDevices.ondevicechange = null;
        };
    }, []);

    const onUserMediaError = () => {
        setIsCameraActive(false);
        setCameraAccess(false)
    }

    return (
        <MotionWrapper className={styles.container}>
            <div className={styles.header} data-is-dark={step === 'Exam'}>

                {step === 'Exam' ?
                    <TypographyLogoLightIcon width={145} height={36} /> :
                    <TypographyLogo width={145} height={36} />}

                {(step === 'PreExam') &&
                    <div className={styles.stream}>
                        <div className={styles.stream_video}>
                            {cameraAccess && <Webcam
                                audio
                                muted
                                ref={webcamRef}
                                onUserMediaError={onUserMediaError}
                                onUserMedia={e => setIsCameraActive(e.active)}
                            />}
                        </div>
                    </div>}
                {step === 'Exam' && <span className={styles.header_exam_title}>
                    {langs?.length > 1 ? selectedLang : langs[0]} exam for {location?.state?.jobTitle}
                </span>}

                <div>
                    <div className={styles.header_actions}>
                        {step === 'Exam' && <Button
                            type='Primary'
                            text='Submit'
                            onClick={submit}
                            loading={isFetching}
                            className={styles.header_actions_btn}
                        />}
                        {(step === 'Exam' || step === 'PreExam') && <Button
                            text='Exit exam'
                            type='Outline-Error'
                            className={styles.header_actions_btn}
                            onClick={() => dispatch(setExitExamModal(true))}
                        />}
                    </div>
                </div>
            </div>

            <div className={styles.main}>
                {step === 'PreExam' && <PreExam
                    cameraAccess={cameraAccess}
                    isCameraActive={isCameraActive}
                    setCameraAccess={setCameraAccess}
                    startScreenRecord={startRecording}
                    isScreenRecordActive={recordingScreen}
                    screenHasRecordError={screenHasRecordError}
                    startExam={startExam}
                    selectedLang={selectedLang}
                    setSelectedLang={setSelectedLang}
                    isFetching={isFetching}
                    action={action}
                />}

                {step === 'Exam' && <Exam
                    ref={webcamRef}
                    userId={userId}
                    counter={counter}
                    questionCount={q}
                    examData={examData}
                    isPaused={breakModal}
                    setAnswer={setAnswer}
                    startWebcam={startCapture}
                    examId={location?.state?.examId}
                />}

                {step === 'KYC' && <KYC
                    userId={userId}
                    examType='coding'
                    profile={profile}
                    jobId={location?.state?.jobId}
                    examId={location?.state?.examId}
                    onSuccess={() => dispatch(setCodingExamStep('PreExam'))}
                />}

                {step === 'ExamEnd' && <ExamEnd />}

                {step === 'CollectingData' &&
                    <CollectingData
                        progress={fileUploadProgress}
                        description='Please remain on this page while we collect your exam data.'
                    />}

                {breakModal && <ExamBreak
                    getNextQuestion={getNextQuestion}
                    isFetching={isFetching}
                />}
            </div>
        </MotionWrapper>
    );
};
